/**
 * CaptionOverlay - renders live translated captions and dub audio controls
 * on top of the video player.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Globe, Captions, Mic2, Radio, VolumeX } from 'lucide-react';
import { useLiveCaptions, type DubClipEntry } from '@/hooks/useLiveCaptions';
import { Button } from '@/components/ui/button';

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  quality: 'excellent' | 'good' | 'beta';
}

interface CaptionOverlayProps {
  streamId: number | string | null;
  isLive: boolean;
  captionsActive: boolean;
  volume: number;
  isMuted: boolean;
  playbackTimeSec?: number;
  isPlaying?: boolean;
  onDubStateChange?: (isDubMutingOriginalAudio: boolean) => void;
}

export function CaptionOverlay({
  streamId,
  isLive,
  captionsActive,
  volume,
  isMuted,
  playbackTimeSec = 0,
  isPlaying = false,
  onDubStateChange,
}: CaptionOverlayProps) {
  const [showSelector, setShowSelector] = useState(false);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [dubEnabled, setDubEnabled] = useState(false);
  const [isDubPlaying, setIsDubPlaying] = useState(false);

  const activeDubAudioRef = useRef<HTMLAudioElement | null>(null);
  const dubQueueRef = useRef<DubClipEntry[]>([]);
  const seenDubClipIdsRef = useRef<Set<string>>(new Set());
  const lastPlaybackTimeRef = useRef<number>(0);

  const { latestCaption, recentCaptions, isActive, selectedLang, setLanguage, recentDubClips, latestDubClip } = useLiveCaptions({
    streamId,
    initialLang: 'en',
    enabled: captionsActive && (captionsEnabled || dubEnabled),
    mode: isLive ? 'live' : 'recording',
    playbackTimeSec,
    isPlaying,
  });

  const shouldMuteOriginalAudio = dubEnabled && selectedLang !== 'en';

  // Keep the parent player muted when a dubbed track is active.
  useEffect(() => {
    onDubStateChange?.(shouldMuteOriginalAudio);
  }, [shouldMuteOriginalAudio, onDubStateChange]);

  useEffect(() => {
    return () => {
      onDubStateChange?.(false);
    };
  }, [onDubStateChange]);

  // Fetch available languages once.
  useEffect(() => {
    if (!streamId || !isLive) return;
    fetch(`/api/streams/${streamId}/captions/languages`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: any) => {
        if (data?.data?.languages) {
          setLanguages(data.data.languages);
        }
      })
      .catch(() => {});
  }, [streamId, isLive]);

  const selectedLangInfo = useMemo(
    () => languages.find((l) => l.code === selectedLang),
    [languages, selectedLang]
  );

  const playbackCaption = useMemo(() => {
    if (isLive) return latestCaption;
    if (!recentCaptions.length) return null;
    const withOffsets = recentCaptions.filter((c) => typeof c.offsetSeconds === 'number') as Array<typeof recentCaptions[number] & { offsetSeconds: number }>;
    if (withOffsets.length === 0) {
      return latestCaption;
    }

    let best = withOffsets[0];
    for (const item of withOffsets) {
      if (item.offsetSeconds <= playbackTimeSec + 0.25) {
        best = item;
      } else {
        break;
      }
    }
    if (playbackTimeSec + 1.5 < best.offsetSeconds) {
      return null;
    }
    return best;
  }, [isLive, latestCaption, playbackTimeSec, recentCaptions]);

  const stopActiveAudio = useCallback(() => {
    if (activeDubAudioRef.current) {
      activeDubAudioRef.current.pause();
      activeDubAudioRef.current.src = '';
      activeDubAudioRef.current = null;
    }
    setIsDubPlaying(false);
  }, []);

  const playNextDubClip = useCallback(async () => {
    if (!dubEnabled || selectedLang === 'en' || activeDubAudioRef.current) {
      return;
    }

    const nextClip = dubQueueRef.current.shift();
    if (!nextClip) {
      setIsDubPlaying(false);
      return;
    }

    const audioSrc = nextClip.audio
      ? `data:${nextClip.mimeType || 'audio/mpeg'};base64,${nextClip.audio}`
      : (nextClip.mediaUrl || '');
    if (!audioSrc) {
      setIsDubPlaying(false);
      void playNextDubClip();
      return;
    }

    const audio = new Audio(audioSrc);
    audio.preload = 'auto';
    audio.volume = isMuted ? 0 : Math.max(0, Math.min(1, volume));

    audio.onended = () => {
      activeDubAudioRef.current = null;
      setIsDubPlaying(false);
      void playNextDubClip();
    };

    audio.onerror = () => {
      activeDubAudioRef.current = null;
      setIsDubPlaying(false);
      void playNextDubClip();
    };

    activeDubAudioRef.current = audio;
    setIsDubPlaying(true);

    try {
      await audio.play();
    } catch {
      activeDubAudioRef.current = null;
      setIsDubPlaying(false);
      void playNextDubClip();
    }
  }, [dubEnabled, isMuted, selectedLang, volume]);

  const enqueueDubClip = useCallback((clip: DubClipEntry | null | undefined) => {
    if (!clip || !clip.id) return;
    if (!dubEnabled) return;
    if (selectedLang === 'en') return;
    if (seenDubClipIdsRef.current.has(clip.id)) return;

    seenDubClipIdsRef.current.add(clip.id);
    dubQueueRef.current.push(clip);

    if (dubEnabled && selectedLang !== 'en') {
      void playNextDubClip();
    }
  }, [dubEnabled, playNextDubClip, selectedLang]);

  // Catch new dub clips as they arrive.
  useEffect(() => {
    for (const clip of recentDubClips) {
      enqueueDubClip(clip);
    }
  }, [enqueueDubClip, recentDubClips]);

  useEffect(() => {
    enqueueDubClip(latestDubClip);
  }, [enqueueDubClip, latestDubClip]);

  // Reset dub playback when toggled off or when the selected language changes.
  useEffect(() => {
    stopActiveAudio();
    dubQueueRef.current = [];
    seenDubClipIdsRef.current = new Set();
  }, [selectedLang, dubEnabled, stopActiveAudio]);

  useEffect(() => {
    if (dubEnabled && selectedLang !== 'en') {
      void playNextDubClip();
    }
  }, [dubEnabled, playNextDubClip, selectedLang]);

  useEffect(() => {
    if (isLive) return;
    const delta = Math.abs(playbackTimeSec - lastPlaybackTimeRef.current);
    const seekDetected = delta > 2.0;
    lastPlaybackTimeRef.current = playbackTimeSec;
    if (!seekDetected || !dubEnabled || selectedLang === 'en') return;

    stopActiveAudio();
    dubQueueRef.current = [];
    if (playbackTimeSec < 2) {
      seenDubClipIdsRef.current = new Set();
    }
  }, [dubEnabled, isLive, playbackTimeSec, selectedLang, stopActiveAudio]);

  useEffect(() => {
    if (isLive || !dubEnabled || selectedLang === 'en' || !isPlaying) return;
    const clips = recentDubClips.filter((clip) =>
      typeof clip.offsetSeconds === 'number' &&
      clip.offsetSeconds >= playbackTimeSec - 0.4 &&
      clip.offsetSeconds <= playbackTimeSec + 0.8
    );
    for (const clip of clips) {
      enqueueDubClip(clip);
    }
  }, [dubEnabled, enqueueDubClip, isLive, isPlaying, playbackTimeSec, recentDubClips, selectedLang]);

  if (!captionsActive) return null;

  return (
    <>
      <div className="absolute top-2 right-2 z-20 rounded-2xl border border-white/10 bg-black/45 p-1.5 backdrop-blur-md shadow-lg">
        <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant={captionsEnabled ? 'default' : 'outline'}
          className="h-7 px-2 text-xs bg-black/40 border-white/20 text-white hover:bg-black/70"
          onClick={() => setCaptionsEnabled((v) => !v)}
          title={captionsEnabled ? 'Disable captions' : 'Enable captions'}
        >
          <Captions className="mr-1 h-3 w-3" />
          CC
        </Button>

        <Button
          size="sm"
          variant={dubEnabled ? 'default' : 'outline'}
          className="h-7 px-2 text-xs bg-black/40 border-white/20 text-white hover:bg-black/70"
          onClick={() => setDubEnabled((v) => !v)}
          title={dubEnabled ? 'Disable dubbing' : 'Enable dubbing'}
        >
          <Mic2 className="mr-1 h-3 w-3" />
          Dub
        </Button>

        {(captionsEnabled || dubEnabled) && (
          <div className="relative">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs bg-black/40 border-white/20 text-white hover:bg-black/70 flex items-center gap-1"
              onClick={() => setShowSelector((v) => !v)}
            >
              <Globe className="w-3 h-3" />
              {selectedLangInfo?.code.toUpperCase() ?? 'EN'}
            </Button>

            {showSelector && (
              <div className="absolute right-0 top-8 z-30 bg-gray-900 border border-white/20 rounded-lg shadow-xl p-1 min-w-[220px] max-h-64 overflow-y-auto">
                <button
                  className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-white/10 flex items-center justify-between ${selectedLang === 'en' ? 'text-blue-400' : 'text-white'}`}
                  onClick={() => {
                    setLanguage('en');
                    setShowSelector(false);
                  }}
                >
                  <span>English (Original audio)</span>
                </button>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded hover:bg-white/10 flex items-center justify-between gap-2 ${selectedLang === lang.code ? 'text-blue-400' : 'text-white'}`}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowSelector(false);
                    }}
                  >
                    <span>
                      {lang.name} <span className="text-white/50">({lang.nativeName})</span>
                    </span>
                    {lang.quality === 'beta' && (
                      <span className="text-[10px] bg-yellow-600/70 text-yellow-200 px-1 rounded">Beta</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        </div>

        <div className="mt-1 flex items-center justify-between gap-2 px-1 pb-0.5 text-[10px] uppercase tracking-[0.18em] text-white/55">
          <span className="flex items-center gap-1">
            <Radio className="h-3 w-3" />
            {dubEnabled ? 'Live dub ready' : captionsEnabled ? 'Captions ready' : 'Standby'}
          </span>
          {shouldMuteOriginalAudio && (
            <span className="flex items-center gap-1 text-amber-200/90">
              <VolumeX className="h-3 w-3" />
              Original muted
            </span>
          )}
        </div>
      </div>

      {captionsEnabled && playbackCaption && (
        <div className="absolute bottom-14 left-0 right-0 z-20 flex justify-center pointer-events-none px-4">
          <div className="rounded-2xl border border-white/10 bg-black/78 px-4 py-2 text-center text-sm leading-snug text-white shadow-xl backdrop-blur-md max-w-[92%]">
            <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-cyan-200/80">
              AI translated caption
            </div>
            {playbackCaption.text}
          </div>
        </div>
      )}

      {dubEnabled && selectedLang !== 'en' && (
        <div className="absolute bottom-14 left-0 right-0 z-20 flex justify-center pointer-events-none">
          <div className="rounded-full border border-white/10 bg-black/55 px-3 py-1 text-xs text-white/70 shadow-lg backdrop-blur-md">
            {isDubPlaying ? 'AI dubbed audio active' : 'Preparing AI dubbed audio...'}
          </div>
        </div>
      )}

      {captionsEnabled && !playbackCaption && isActive && (
        <div className="absolute bottom-14 left-0 right-0 z-20 flex justify-center pointer-events-none">
          <div className="rounded-full border border-white/10 bg-black/55 px-3 py-1 text-xs text-white/60 shadow-lg backdrop-blur-md">
            Waiting for captions...
          </div>
        </div>
      )}
    </>
  );
}
